/**
 * Me resource: the caller's own display profile and admin roles.
 *
 * `fetchMyRolesAsync` is the imperative accessor the Datastore's user-init path
 * uses. Plain function rather than a hook because that call site runs outside
 * React; it takes a `Datastore` to reach that instance's query client and silo
 * key, and the caller's own `profileKey` (always their auth `userId`, which
 * every call site already holds from the auth session). It fetches through
 * `QueryClient.fetchQuery` against the same `profiles.get` query options
 * `useProfile` reads rather than the raw API client, so warming the roles also
 * warms the profile every `useMyProfile()` on the page then reads from cache.
 */
import type { IsoLocale } from '@repo/shared/locale';
import type { UserProfile } from '@repo/shared/orpc/types';
import { useQuery } from '@tanstack/react-query';
import { useApiQueryUtils } from 'system/api-client-context';
import type { Datastore } from 'util/datastore';
import { usePersonaKey } from 'util/datastore';

import { useProfile } from './profile';
import { siloParamsOf } from './silo-params';

/**
 * The caller's admin roles, fetched through the shared profile cache. Empty
 * for a login that has no roles, and for one that has no saved profile yet.
 */
export async function fetchMyRolesAsync(datastore: Datastore, profileKey: string): Promise<string[]> {
    const profile = await datastore
        .getQueryClient()
        .fetchQuery(datastore.getApiQueryUtils().profiles.get.queryOptions({ input: { ...siloParamsOf(datastore), profileKey } }));
    return profile.roles ?? [];
}

/**
 * The current caller's own display profile — `null` if not signed in. Reads
 * `useProfile` against the caller's own key, the same as any other profile
 * read, so it shares the cache `fetchMyRolesAsync` warms at login rather than
 * issuing a second fetch for the common case of mounting after that initial
 * load completes.
 */
export function useMyProfile(): UserProfile | null {
    const personaKey = usePersonaKey();
    return useProfile(personaKey).profile;
}

/**
 * Whether the profile has yet to finish initial setup, which `profiles.get`
 * reports as `hasProfile: false`.
 *
 * `undefined` until the profile loads, and `false` without a profile key.
 */
export function useNeedsProfileSetup({
    siloKey,
    profileKey,
    locale,
}: {
    siloKey: string;
    profileKey: string | null;
    locale: IsoLocale;
}): boolean | undefined {
    const utils = useApiQueryUtils();
    const { data } = useQuery({
        ...utils.profiles.get.queryOptions({
            input: {
                siloKey,
                profileKey: profileKey ?? '',
                locale,
            },
        }),
        enabled: !!profileKey,
    });

    if (!profileKey) {
        return false;
    }
    return data ? data.hasProfile === false : undefined;
}
